import { BaseApiClient } from '../base-client'
import {
  API_ROUTES,
  ApiResponse,
  MetadataOnlyIntegrationType,
  interpolateRoute,
  type RentManagerServiceManagerStatusesResponse,
  type RentManagerLocationOverview,
} from '@super/shared-types'
import { withErrorHandling } from '../../utils'

export interface RentManagerAdminMetadata {
  configId: string
  defaultStatusId?: string
  locationIds?: number[]
}

/**
 * Rent Manager Integration API Client
 * Handles Rent Manager-specific API operations (metadata-only integration)
 */
export class RentManagerClient extends BaseApiClient {
  /**
   * Disconnect Rent Manager integration (regular user)
   */
  async disconnect(): Promise<void> {
    await withErrorHandling(
      async () => {
        const url = interpolateRoute(
          API_ROUTES.INTEGRATIONS.METADATA_ONLY.DELETE,
          { integrationType: MetadataOnlyIntegrationType.RENT_MANAGER },
        )
        return await this.request<ApiResponse<{ message: string }>>(url, {
          method: 'DELETE',
        })
      },
      {
        errorContext: 'Error disconnecting Rent Manager integration',
      },
    )
  }

  /**
   * [ADMIN] Create metadata-only integration for Rent Manager
   */
  async createAdminMetadata(
    companyId: string,
    metadata: RentManagerAdminMetadata,
  ): Promise<void> {
    await withErrorHandling(
      async () => {
        const url = interpolateRoute(
          API_ROUTES.ADMIN.INTEGRATIONS.METADATA_ONLY.CREATE,
          { integrationType: MetadataOnlyIntegrationType.RENT_MANAGER },
        )
        return await this.request<ApiResponse<{ message: string }>>(
          `${url}?companyId=${companyId}`,
          {
            method: 'POST',
            body: JSON.stringify({ metadata }),
          },
        )
      },
      {
        errorContext: 'Error creating Rent Manager integration',
      },
    )
  }

  /**
   * [ADMIN] Update metadata-only integration for Rent Manager
   */
  async updateAdminMetadata(
    companyId: string,
    metadata: RentManagerAdminMetadata,
  ): Promise<void> {
    await withErrorHandling(
      async () => {
        const url = interpolateRoute(
          API_ROUTES.ADMIN.INTEGRATIONS.METADATA_ONLY.UPDATE,
          { integrationType: MetadataOnlyIntegrationType.RENT_MANAGER },
        )
        return await this.request<ApiResponse<{ message: string }>>(
          `${url}?companyId=${companyId}`,
          {
            method: 'PATCH',
            body: JSON.stringify({ metadata }),
          },
        )
      },
      {
        errorContext: 'Error updating Rent Manager integration',
      },
    )
  }

  /**
   * Get Rent Manager service manager statuses (regular user)
   */
  async getStatuses(): Promise<RentManagerServiceManagerStatusesResponse> {
    const result = await withErrorHandling(
      async () => {
        const response = await this.request<
          ApiResponse<RentManagerServiceManagerStatusesResponse>
        >(API_ROUTES.INTEGRATIONS.RENT_MANAGER.STATUSES)
        return response.data || []
      },
      {
        errorContext: 'Error fetching Rent Manager statuses',
      },
    )
    return result || []
  }

  /**
   * [ADMIN] Get Rent Manager service manager statuses for a specific company
   */
  async getAdminStatuses(
    companyId: string,
  ): Promise<RentManagerServiceManagerStatusesResponse> {
    const result = await withErrorHandling(
      async () => {
        const response = await this.request<
          ApiResponse<RentManagerServiceManagerStatusesResponse>
        >(
          `${API_ROUTES.ADMIN.INTEGRATIONS.RENT_MANAGER.STATUSES}?companyId=${companyId}`,
        )
        return response.data || []
      },
      {
        errorContext: 'Error fetching Rent Manager statuses',
      },
    )
    return result || []
  }

  /**
   * [ADMIN] Get Rent Manager locations (databases) for a specific company
   */
  async getAdminLocations(
    companyId: string,
  ): Promise<RentManagerLocationOverview[]> {
    const result = await withErrorHandling(
      async () => {
        const response = await this.request<
          ApiResponse<RentManagerLocationOverview[]>
        >(
          `${API_ROUTES.ADMIN.INTEGRATIONS.RENT_MANAGER.LOCATIONS}?companyId=${companyId}`,
        )
        return response.data || []
      },
      {
        errorContext: 'Error fetching Rent Manager locations',
      },
    )
    return result || []
  }

  /**
   * Update non-sensitive metadata fields (regular user)
   */
  async updateMetadata(metadata: Record<string, unknown>): Promise<void> {
    await withErrorHandling(
      async () => {
        const url = interpolateRoute(
          API_ROUTES.INTEGRATIONS.METADATA_ONLY.UPDATE,
          { integrationType: MetadataOnlyIntegrationType.RENT_MANAGER },
        )
        return await this.request<ApiResponse<{ message: string }>>(url, {
          method: 'PATCH',
          body: JSON.stringify({ metadata }),
        })
      },
      {
        errorContext: 'Error updating Rent Manager settings',
      },
    )
  }

  /**
   * [ADMIN] Disconnect Rent Manager integration for a specific company
   */
  async disconnectAdmin(companyId: string): Promise<void> {
    await withErrorHandling(
      async () => {
        const url = interpolateRoute(
          API_ROUTES.ADMIN.INTEGRATIONS.METADATA_ONLY.DELETE,
          { integrationType: MetadataOnlyIntegrationType.RENT_MANAGER },
        )
        return await this.request<ApiResponse<{ message: string }>>(
          `${url}?companyId=${companyId}`,
          {
            method: 'DELETE',
          },
        )
      },
      {
        errorContext: 'Error disconnecting Rent Manager integration',
      },
    )
  }
}

// Export singleton instance
export const rentManagerClient = new RentManagerClient()
